import React, {useCallback, useEffect, useState, useMemo} from 'react';
import moment from 'moment';
import {SchoolService} from '../../modules/Schools';
import {
  Tabs,
  QuestionModal,
  QuestionCard,
  MCQModal,
} from '@PikaDoApp/pikado-web-dsm';
import SimpleLoader from '../../components/SimpleLoader/SimpleLoader';

export const ClassNextPrecExercises: React.FC<{selectedClassId: string}> = ({
  selectedClassId,
}) => {
  const schoolService = useMemo(() => new SchoolService(), []);
  const [studentExercises, setStudentExercises] = useState<any>([]);
  const [selectedDateExe, setSelectedDateExe] = useState('');
  const [isQuestionModalOpen, setIsQuestionModalOpen] = useState<any>(false);
  const [openDailyExeModal, setOpenDailyExeModal] = useState<any>();
  const [selectedQuestion, setSelectedQuestion] = useState<any>();
  const [selectedDailyesExe, setSelectedDailyExe] = useState<any>();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchclassExeforMultiDayes = async () => {
    try {
      const response =
        await schoolService.getClassExeForPrevSecDays(selectedClassId);
      if (response) {
        const selected = setSelectedDayesExeHelper(response, selectedClassId);
        setStudentExercises(response);
        setSelectedDateExe(selected);
      }
      setLoading(false);
    } catch (e) {
      setLoading(false);
    }
  };

  const setSelectedDayesExeHelper = useCallback(
    (exercises: any, classId: string) => {
      const today = new Date().toISOString().split('T')[0];
      const allDays = exercises;
      if (allDays.length === 0) return '';

      const selectedDay = allDays.find((e: any) => e.date === today)
        ? today
        : allDays[0].date;
      return `${selectedDay}-${classId}`; // Include classId in the selected date key
    },
    [],
  );

  useEffect(() => {
    // Reset all states when class ID changes
    //  setStudentExercises([]); // Use empty array instead of undefined
    setLoading(true);
    setSelectedQuestion(undefined);
    setSelectedDailyExe(undefined);

    setCurrentQuestionIndex(0);

    // Fetch new data if class ID is provided
    if (selectedClassId) {
      fetchclassExeforMultiDayes();
    }
  }, [selectedClassId]);

  const daysTabsItems =
    studentExercises?.map((dailyExe, idx) => ({
      key: `${dailyExe.date}-${selectedClassId}`, // Include classId in tab key
      label: moment(dailyExe.date, 'YYYY-MM-DD').format('MMM DD, YYYY'),
      children: (
        <section
          className="container flex items-start justify-center w-full px-4 py-8 mx-auto mt-5 "
          key={`section-${dailyExe.date}-${selectedClassId}-${idx}`}>
          <div className="flex flex-wrap w-full gap-y-4">
            {dailyExe.items?.length > 0 &&
              dailyExe.items.map((item, itemIdx) => (
                <QuestionCard
                  key={`card-${item.id || itemIdx}-${selectedClassId}-${
                    dailyExe.date
                  }`}
                  {...item}
                  url={item.thumbnail}
                  isVideo={item.media?.[0]?.type?.toLowerCase() === 'video'}
                  showRedButton={item.questions?.length > 1}
                  onClick={() => {
                    setSelectedDailyExe(item);
                    setOpenDailyExeModal(true);
                  }}
                />
              ))}
          </div>
        </section>
      ),
    })) || [];

  const onExeDateTabChange = useCallback((key: string) => {
    setSelectedDateExe(key);
  }, []);

  const formatComprehensionTitleText = (titleText: string): string => {
    if (!titleText) return '';
    const replacedKeys = new Set<string>();
    const choicesPattern = /choices\d+/gi;

    let output = titleText.replace(choicesPattern, (match) => {
      const normalized = match.toLowerCase();

      if (replacedKeys.has(normalized)) {
        return ''; // remove duplicate
      }

      replacedKeys.add(normalized);
      return '{blank}';
    });

    output = output.replace(/\s+/g, ' ').trim();

    return output;
  };

  return (
    <div
      style={{
        margin: '10px',
        padding: '10px',
      }}>
      {loading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1000,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <SimpleLoader isLoading={true} title="Switching..." />
        </div>
      )}
      {studentExercises && studentExercises.length > 0 && (
        <>
          <Tabs
            key={selectedClassId} // Force Tabs to remount when class changes
            items={daysTabsItems}
            activeKey={selectedDateExe}
            onChange={onExeDateTabChange}
          />
          {/* daily exercise test content displaying in the first modal */}
          {selectedDailyesExe && (
            <QuestionModal
              open={openDailyExeModal}
              onClose={() => {
                setOpenDailyExeModal(false);
                setSelectedDailyExe(undefined);
              }}
              title={selectedDailyesExe?.name}
              horizontal={false}
              videoUrl={
                selectedDailyesExe?.media?.[0]?.type?.toLowerCase() === 'video' &&
                selectedDailyesExe?.media?.[0]?.url !== ''
                  ? selectedDailyesExe?.media?.[0]?.url
                  : null
              }
              imageUrl={
                selectedDailyesExe?.media?.[0]?.type?.toLowerCase() === 'photo' &&
                selectedDailyesExe?.media?.[0]?.url !== ''
                  ? selectedDailyesExe?.media?.[0]?.url
                  : selectedDailyesExe?.thumbnail
              }
              SecondaryText={selectedDailyesExe?.summary}
              buttonText={'Show Questions'}
              onButtonClick={() => {
                setIsQuestionModalOpen(true);
                setOpenDailyExeModal(false);
                const qs = selectedDailyesExe?.questions;
                console.log(qs);
                setSelectedQuestion(qs);
              }}
            />
          )}

          {selectedQuestion && selectedQuestion[currentQuestionIndex] && (
            <div key={selectedQuestion[currentQuestionIndex]?.id}>
              {selectedQuestion[currentQuestionIndex]?.type ===
              'COMPREHENSION' ? (
                <MCQModal
                  open={isQuestionModalOpen}
                  onClose={() => {
                    setIsQuestionModalOpen(false);
                    setCurrentQuestionIndex(0);
                  }}
                  title={
                    selectedQuestion[currentQuestionIndex]?.summary ??
                    selectedQuestion[currentQuestionIndex]?.description
                  }
                  question={formatComprehensionTitleText(
                    selectedQuestion[currentQuestionIndex]?.questionText,
                  )}
                  choices={
                    selectedQuestion[currentQuestionIndex]?.payload?.blanks?.[0]
                      ?.choices || []
                  }
                  onAnswer={() => console.log('test')}
                  onSubmit={() => {
                    // Check if this is the last question
                    if (
                      currentQuestionIndex + 1 >=
                      selectedDailyesExe?.questions?.length
                    ) {
                      // Last question - close the modal
                      setIsQuestionModalOpen(false);
                      setSelectedDailyExe(null);
                      setCurrentQuestionIndex(0);
                    } else {
                      // Not the last question - show next question
                      setIsQuestionModalOpen(true);
                      setOpenDailyExeModal(false);
                      setCurrentQuestionIndex(currentQuestionIndex + 1);
                    }
                  }}
                  submitButtonText={
                    currentQuestionIndex + 1 <
                    selectedDailyesExe?.questions?.length
                      ? 'Show next question'
                      : 'Finish'
                  }
                  questionIndex={currentQuestionIndex + 1}
                  totalQuestions={selectedDailyesExe?.questions?.length || 0}
                  videoUrl={
                    selectedQuestion[
                      currentQuestionIndex

                    ]?.payload?.media?.[0].type?.toLowerCase() === 'video'
                      ? selectedQuestion[currentQuestionIndex]?.payload
                          ?.media?.[0]?.url
                      : undefined
                  }
                  imageSrc={
                    selectedQuestion[currentQuestionIndex]?.payload?.media?.[0]
                      ?.url
                  }
                  imageAlt={'MCQ Question'}
                  // Multi-blank props
                  isMultiBlank={
                    selectedQuestion[currentQuestionIndex]?.payload?.blanks
                      ?.length > 1
                  }
                  blankGroups={
                    selectedQuestion[currentQuestionIndex]?.payload?.blanks
                  }
                  disabled={false}
                  onMultiBlankAnswer={(blankKey: string, choice: string) => {}}
                  //   multiBlankSelections={multiBlankSelections}
                />
              ) : (
                //read sentences
                <QuestionModal
                  open={isQuestionModalOpen}
                  onClose={() => {
                    console.log('closed');
                    setIsQuestionModalOpen(false);
                    setSelectedDailyExe(null);
                    setCurrentQuestionIndex(0);
                  }}
                  title={
                    selectedQuestion[currentQuestionIndex]?.summary ??
                    selectedQuestion[currentQuestionIndex]?.description
                  }
                  horizontal={false} // have multiple questions
                  videoUrl={
                    selectedQuestion[
                      currentQuestionIndex

                    ]?.payload?.media?.[0]?.type?.toLowerCase() === 'video'
                      ? selectedQuestion[currentQuestionIndex]?.payload
                          ?.media?.[0]?.url
                      : undefined
                  }
                  imageUrl={
                    selectedQuestion[
                      currentQuestionIndex
                    ]?.payload?.media?.[0]?.type?.toLowerCase() === 'photo'
                      ? selectedQuestion[currentQuestionIndex]?.payload
                          ?.media?.[0]?.url
                      : selectedDailyesExe?.thumbnail
                  }
                  //TODO: add text in new line in the DSM
                  //@ts-ignore
                  SecondaryText={selectedQuestion[currentQuestionIndex]?.questionText}
                  buttonText={
                    currentQuestionIndex + 1 <
                    selectedDailyesExe?.questions?.length
                      ? 'Show next question'
                      : 'Finish'
                  }
                  onButtonClick={() => {
                    // Check if this is the last question
                    if (
                      currentQuestionIndex + 1 >=
                      selectedDailyesExe?.questions?.length
                    ) {
                      // Last question - close the modal
                      setIsQuestionModalOpen(false);
                      setSelectedDailyExe(null);
                      setCurrentQuestionIndex(0);
                    } else {
                      // Not the last question - show next question
                      setIsQuestionModalOpen(true);
                      setOpenDailyExeModal(false);
                      setCurrentQuestionIndex(currentQuestionIndex + 1);
                    }
                  }}
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
